/**
 * Shared module-level dedup Set for step-level lifecycle events
 * emitted by `useEntryPointEventFiring` (entry/non-entry mount and
 * intersect fires).
 *
 * Two roles today:
 *
 * 1. **Back-button re-entry dedup** — revisiting a step within the
 *    same provider session (back button or parent re-key without a
 *    hard nav) does not re-fire its lifecycle events. Matches the
 *    legacy in-flow behavior and is correct for funnel analytics.
 * 2. **`Trackable` co-tenancy** — `Trackable.tsx` stores its own
 *    Puck-node-id-shaped `stableKey`s here (e.g.
 *    `cfg-1::0::Select-degree`) alongside the `entryPoint:*` keys
 *    built by {@link buildEntryPointKey}. Isolation comes from the
 *    `entryPoint:` prefix — Trackable's keys carry no prefix of
 *    their own and can't collide with it.
 *
 * Fresh-session correctness: callers should pass a per-provider-
 * session `flowSessionId` so a brand-new mount of the same flow
 * (SPA navigation away/back, widget reopen, parent re-key that
 * destroys the provider) starts with a clean slate and fires the
 * lifecycle sequence again. Within a single session the keys still
 * dedupe back-button revisits as expected.
 *
 * @see {@link Trackable} — its dedup docblock carries the broader
 *   string-keyed vs. DOM-keyed dedup story and pairs with the
 *   still-present `FIRED_NODES` WeakSet.
 */
const firedEntryEventKeys = new Set<string>();

/**
 * Wire event names participating in step-level lifecycle dedup:
 *
 * | Step shape | Mount                            | Intersect                     |
 * | ---------- | -------------------------------- | ----------------------------- |
 * | entry      | `ElementLoaded`, `ProductLoaded` | `ProductViewed`, `FormViewed` |
 * | non-entry  | `ElementLoaded` (§7.3)           | `ElementViewed` (§7.3)        |
 *
 * Entry and non-entry `ElementLoaded` share a key segment — `stepId`
 * is exclusively one shape per flow, so `(scope, stepId)` in
 * {@link buildEntryPointKey} already partitions instances. Wire
 * webElement shapes diverge per payload builder; the dedup key only
 * needs the wire event name.
 */
export type EntryPointEventName =
  | 'ElementLoaded'
  | 'ProductLoaded'
  | 'ProductViewed'
  | 'FormViewed'
  | 'ElementViewed';

export interface EntryPointKeyContext {
  /**
   * Per-provider-session scope. Owned by `DynamicExperienceProvider`
   * — a fresh value is generated on each mount, and rotated when
   * `handleRestart` resets the flow. Including it in the key shape
   * ensures a brand-new mount of the same flow (SPA navigation,
   * widget reopen, parent re-key) emits a fresh lifecycle sequence
   * instead of being suppressed by stamps from a prior session.
   *
   * Optional so callers that pre-stamp keys outside the provider
   * (test fixtures, ad-hoc unit harnesses) can still match the
   * legacy key shape.
   */
  readonly flowSessionId?: string;
  /** Preferred scope — `currentStep.flowId`. */
  readonly flowId?: string;
  /**
   * Fallback scope when `flowId` is absent (rare; DSL configs without
   * a parent flow) — `TrackingProvider.dslConfigId`.
   */
  readonly dslConfigId?: string;
  /** Per-step identity — `currentStep.id`. */
  readonly stepId?: string;
}

/**
 * Build the canonical key:
 * `entryPoint:<eventName>:<scope>:<stepId>[:<flowSessionId>]`
 * where `scope` is `flowId` ?? `dslConfigId`. Missing components
 * fall back to `'-'` to keep the shape length-stable. The
 * `flowSessionId` segment is appended when present so legacy
 * unscoped keys (no session) keep their original shape.
 *
 * The `entryPoint:` prefix isolates this namespace from
 * `Trackable.tsx`'s Puck-node-id keys sharing the same Set.
 */
export const buildEntryPointKey = (
  eventName: EntryPointEventName,
  context: EntryPointKeyContext,
): string => {
  const scope = context.flowId ?? context.dslConfigId ?? '-';
  const step = context.stepId ?? '-';
  const base = `entryPoint:${eventName}:${scope}:${step}`;
  return context.flowSessionId ? `${base}:${context.flowSessionId}` : base;
};

/**
 * `true` if the key was already stamped this session. Generic over
 * key shape — the Set holds both `entryPoint:*` keys and `Trackable`'s
 * unprefixed Puck-node-id `stableKey`s; the `entryPoint:` prefix
 * keeps them from colliding.
 */
export const hasFiredKey = (key: string): boolean =>
  firedEntryEventKeys.has(key);

/** Stamp the key. Idempotent. */
export const addFiredKey = (key: string): void => {
  firedEntryEventKeys.add(key);
};

/**
 * Test-only — production code MUST NOT call this. Clears every entry
 * (including `Trackable`'s `stableKey`s) so tests across both modules
 * start from the "never fired" precondition.
 * `Trackable.__resetTrackableDedupForTests` delegates here.
 */
export function __resetForTests(): void {
  firedEntryEventKeys.clear();
}
